import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, Calendar, Package, LogOut, Plus } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [bookingsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/bookings/my`, config),
        axios.get(`${API}/packages/my`, config)
      ]);

      setBookings(bookingsRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      confirmed: 'bg-green-500/20 text-green-300 border-green-500/30',
      completed: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900">
      {/* Header */}
      <header className="border-b border-purple-500/30 bg-purple-900/40 backdrop-blur-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8 text-purple-300" />
              <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>Divine Quanta</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-purple-200">Welcome, {user.name}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-purple-200 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Quick Actions */}
        <div className="mb-12">
          <Button
            data-testid="new-booking-btn"
            onClick={() => navigate('/book')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 rounded-full text-lg font-semibold spiritual-glow"
          >
            <Plus className="w-5 h-5 mr-2" />
            Book New Session
          </Button>
        </div>

        {/* Active Packages */}
        {packages.length > 0 && (
          <div className="mb-12" data-testid="active-packages-section">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              <Package className="w-8 h-8 mr-3 text-purple-300" />
              Your Active Packages
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <Card key={pkg.id} className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid={`package-card-${pkg.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-white capitalize">{pkg.package_type.replace('_', ' ')}</h3>
                    <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">Active</span>
                  </div>
                  <div className="space-y-2 text-purple-200">
                    <p>Total Sessions: {pkg.total_sessions}</p>
                    <p>Used: {pkg.used_sessions}</p>
                    <p className="text-2xl font-bold text-purple-300">{pkg.remaining_sessions} Sessions Left</p>
                    <p className="text-sm">Expires: {format(new Date(pkg.expiry_date), 'MMM dd, yyyy')}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Booking History */}
        <div data-testid="bookings-history-section">
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            <Calendar className="w-8 h-8 mr-3 text-purple-300" />
            Your Bookings
          </h2>
          
          {bookings.length === 0 ? (
            <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-12 text-center" data-testid="no-bookings-message">
              <p className="text-purple-200 text-lg mb-4">You haven't booked any sessions yet</p>
              <Button
                data-testid="first-booking-btn"
                onClick={() => navigate('/book')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Book Your First Session
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id} className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid={`booking-card-${booking.id}`}>
                  <div className="grid md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-purple-400 text-sm mb-1">Booking Date</p>
                      <p className="text-white font-semibold">{format(new Date(booking.booking_date), 'MMM dd, yyyy')}</p>
                    </div>
                    <div>
                      <p className="text-purple-400 text-sm mb-1">Session Type</p>
                      <p className="text-white font-semibold capitalize">{booking.session_type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-purple-400 text-sm mb-1">Status</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm border ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-purple-400 text-sm mb-1">Payment</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm border ${getStatusColor(booking.payment_status)}`}>
                        {booking.payment_status}
                      </span>
                    </div>
                  </div>
                  {booking.session_date && (
                    <div className="mt-4 pt-4 border-t border-purple-500/30">
                      <p className="text-purple-400 text-sm">Scheduled Session</p>
                      <p className="text-white font-semibold">{format(new Date(booking.session_date), 'MMM dd, yyyy - hh:mm a')}</p>
                    </div>
                  )}
                  {booking.therapist_notes && (
                    <div className="mt-4 pt-4 border-t border-purple-500/30">
                      <p className="text-purple-400 text-sm mb-2">Therapist Notes</p>
                      <p className="text-purple-200">{booking.therapist_notes}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
